import { type Auth0ContextInterface, type AuthorizationParams, type User } from '@auth0/auth0-react';
import { OAuthAccessDeniedCode } from '@/api/error-codes';
import {
  type AuthenticationType,
  type AuthenticationTypeKey,
  type AuthenticationTypeMap,
} from '@/components/Analytics/events.types';
import { setDidJustAuthenticate } from '@/components/Analytics/tracking';
import { type SessionExceptionModalReason } from '@/components/Logout/SessionExceptionModal';
import { reportError } from '@/helpers/errors/report';

/**
 * Track the login event when the user logs in or signs up.
 * It is necessary to do it here instead of Auth0 actions for AdWord conversion tracking to function properly.
 */
export function onExecutePostLogin(user: User) {
  if (!(user.sub && user.email && typeof user.loginCount === 'number')) {
    reportError(new Error('Auth0 user missing claims subject, email or loginCount'), {
      domain: 'auth',
      level: 'fatal',
    });
    return;
  }

  const authProviderName = getConnectionReadableName(user.sub);

  if (!authProviderName) {
    reportError(
      new Error(
        'Unsupported authentication string type. ' +
          'Silence this error by implementing handling for this new type — check Additional Data: sub',
      ),
      {
        domain: 'auth',
        level: 'fatal',
        extra: { sub: user.sub },
      },
    );
    return;
  }

  setDidJustAuthenticate();
}

export function getConnectionReadableName(sub: string): AuthenticationType | void {
  const type = getConnectionName(sub);
  if (!type) {
    return;
  }

  /* eslint-disable @cspell/spellchecker */
  const authTypeMap: AuthenticationTypeMap = {
    ad: 'LDAP',
    adfs: 'ADFS',
    'auth0-adldap': 'Auth0 AD/LDAP',
    email: 'One-time Email Code',
    github: 'GitHub',
    'google-apps': 'Google Workspace',
    'google-oauth2': 'Google',
    ip: 'IP',
    office365: 'Office 365',
    oidc: 'OIDC',
    okta: 'Okta',
    pingfederate: 'PingFederate',
    samlp: 'SAML',
    sharepoint: 'SharePoint',
    waad: 'Windows Azure AD',
  };
  /* eslint-enable @cspell/spellchecker */

  return authTypeMap[type];
}

export function getConnectionName(sub: string) {
  const type = sub.split('|')[0];
  if (type) {
    return type as AuthenticationTypeKey;
  }
  return undefined;
}

export const ConsumerConnectionStrategies: ReadonlySet<string> = new Set(['google-oauth2', 'github', 'email', 'auth0']);

export type MfaEnrollmentType = 'otp' | 'recovery-code' | 'webauthn-roaming';

/**
 * IMPORTANT: Max of 10 custom parameters are allowed by Auth0.
 *
 * Custom parameters for the Auth0 authorization request.
 * These parameters are prefixed with `ext-` to avoid conflicts with standard Auth0 parameters.
 * They are used to pass additional information to the authorization server.
 */
export type CustomAuthorizationParams = AuthorizationParams & {
  'ext-back_link': string;
  'ext-cdn': string;
  'ext-home-url': string;
  'ext-onetrust-domain-id': string;
  'ext-segment-write-key': string;
  'ext-incident_mode'?: string;
  'ext-aerr'?: string;
  'ext-linking-token'?: string;
  'ext-mfa-enrollment'?: MfaEnrollmentType;
};

export function assertAuth0ExtParamsLength(params: CustomAuthorizationParams) {
  const extParams = Object.keys(params).filter((key) => key.startsWith('ext-'));
  if (extParams.length > 10) {
    throw new Error(
      'Auth0 custom parameters limit exceeded. ' +
        `Max of 10 custom parameters are allowed. Current count: ${extParams.length}`,
    );
  }
}

export function isSessionAuthError(authError: string | undefined): authError is SessionExceptionModalReason {
  return (
    authError !== undefined &&
    ['unknown', 'invalid', 'expired', ...Object.values(OAuthAccessDeniedCode)].includes(authError)
  );
}

/**
 * The `RouterProvider` always receives the Auth0 context after `isLoading` is done, so we can safely omit it.
 */
export type AuthContext = Omit<Auth0ContextInterface, 'isLoading'>;
